'use client';

import { useFormTemplateKeysInfinite } from '@commenda/api';
import {
  LoadingIndicator,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import {
  FORM_BUILDER_DEBOUNCE_MS,
  FORM_BUILDER_TEMPLATE_KEYS_ON_SELECT_DEFAULT_PAGE_SIZE,
} from '@commenda/utils/form-builder';
import { Check, ChevronDown, Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';

interface AsyncKeyComboboxProps {
  value: {
    value: string;
    label: string;
  } | null;
  setValue: (val: { value: string; label: string } | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  onAddKey?: () => void;
}

export function AsyncKeyCombobox({
  value,
  setValue,
  placeholder = 'Select key',
  searchPlaceholder = 'Search keys...',
  disabled = false,
  id,
  className,
  onAddKey,
}: AsyncKeyComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, FORM_BUILDER_DEBOUNCE_MS);

  // Fetch keys with pagination
  const {
    keys,
    isLoading,
    isLoadingMore,
    hasNextPage,
    loadMore,
    mutate,
    totalCount,
  } = useFormTemplateKeysInfinite({
    searchTerm: debouncedSearch || undefined,
    pageSize: FORM_BUILDER_TEMPLATE_KEYS_ON_SELECT_DEFAULT_PAGE_SIZE,
    enabled: true,
  });

  // Build options with "None" prepended
  // remove duplicates
  let options: { value: string; label: string }[] = useMemo(
    () => [
      { value: null, label: 'Unlink' },
      ...(value ? [{ value: value.value, label: value.label }] : []),
      ...keys.map((key) => ({
        value: key.key,
        label: key.name,
      })),
    ],
    [value, keys]
  );

  options = options.filter(
    (option, index, self) =>
      index === self.findIndex((t) => t.value === option.value)
  );

  const handleAddKey = useCallback(async () => {
    await onAddKey?.();
    await mutate();
  }, [onAddKey, mutate]);
  // Find selected option
  const selectedOption = options.find((opt) => opt.value === value?.value);

  // Handle selection
  const handleSelect = useCallback(
    (selectedValue: string) => {
      const selectedOption = options.find((opt) => opt.value === selectedValue);
      setValue(
        selectedValue === value?.value
          ? null
          : selectedOption
            ? { value: selectedOption.value, label: selectedOption.label }
            : null
      );
      setOpen(false);
      setSearchQuery('');
    },
    [setValue, value, options]
  );

  // Handle load more click
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isLoading && !isLoadingMore) {
      loadMore();
    }
  }, [hasNextPage, isLoading, isLoadingMore, loadMore]);

  // Refetch when popover opens, reset search when it closes
  useEffect(() => {
    if (open) {
      mutate();
    } else {
      setSearchQuery('');
    }
  }, [open, mutate]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-expanded={open}
          className={cn(
            'flex h-10 w-full box-border items-center justify-between rounded-md px-xl py-2.5 bg-bg-primary text-text-primary txt-small-regular ring-0 transition-colors [&>span]:line-clamp-1',
            'disabled:cursor-not-allowed disabled:bg-bg-disabled-subtle disabled:text-text-disabled disabled:pointer-events-none appearance-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset',
            'border border-border-primary hover:border-border-primary-hover focus-visible:ring-border-primary-hover focus-visible:border-border-primary-hover disabled:border-border-disabled',
            'aria-expanded:ring-2 aria-expanded:ring-fg-brand aria-expanded:border-transparent',
            !value &&
              'aria-expanded:ring-1 aria-expanded:ring-border-primary-hover',
            className
          )}
        >
          <span
            className={cn(
              'flex-grow text-left truncate',
              !selectedOption?.value && 'text-text-placeholder'
            )}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 ml-2 text-fg-quaternary" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{ width: 'var(--radix-popper-anchor-width)' }}
      >
        <div className="flex flex-col">
          {/* Search input */}
          <div className="flex items-center border-b border-border-secondary px-3">
            <Search className="h-4 w-4 shrink-0 text-fg-quaternary" />
            <input
              type="text"
              className="flex h-10 w-full bg-transparent py-3 px-2 txt-small-regular text-text-primary placeholder:text-text-placeholder focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 border-none ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-0"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {isLoading && <LoadingIndicator size={16} />}
          </div>

          {/* Add key option */}
          {onAddKey && (
            <div
              className="flex cursor-pointer select-none items-center px-3 py-2 txt-small-regular text-fg-brand hover:bg-bg-secondary border-b border-border-secondary"
              onClick={() => {
                handleAddKey();
                setOpen(false);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              <span>Add key</span>
            </div>
          )}

          {/* Options list */}
          <div
            className="max-h-60 overflow-y-auto"
            onWheel={(e) => e.stopPropagation()}
          >
            {/* Empty state */}
            {!isLoading && options.length === 1 && debouncedSearch && (
              <div className="py-6 text-center txt-small-regular text-text-tertiary">
                No keys found.
              </div>
            )}

            {/* Options */}
            {options.map((option) => (
              <div
                key={option.value ?? 'none'}
                className={cn(
                  'relative flex cursor-pointer select-none items-center px-3 py-2 txt-small-regular',
                  'hover:bg-bg-secondary focus:bg-bg-secondary',
                  value?.value === option.value && 'bg-bg-secondary'
                )}
                onClick={() => handleSelect(option.value)}
              >
                <span className="flex-grow truncate">{option.label}</span>
                <Check
                  className={cn(
                    'ml-auto h-4 w-4 shrink-0',
                    value?.value === option.value ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </div>
            ))}

            {/* Load More button */}
            {hasNextPage && (
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="w-full py-2 px-3 txt-small-medium text-fg-brand hover:bg-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed border-t border-border-secondary"
              >
                {isLoadingMore ? (
                  <span className="flex items-center justify-center gap-2">
                    <LoadingIndicator size={16} />
                    Loading...
                  </span>
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <span className="txt-small-medium">Load more</span>
                    <span className="text-xs text-text-tertiary">
                      ({totalCount - keys.length} remaining)
                    </span>
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
